import { Box, Modal, TextField, Typography } from "@mui/material";
import AppLayout from "../layouts/AppLayout";
import { useEffect, useMemo, useState } from "react";
import { sanitize } from "dompurify";
import appApi from "../api";
import moment from "moment";
import IconContentButton from "../components/UserInput/Buttons/IconContentButton";
import SingleMultipleResponse from "../components/Survey/previewResponse/SingleMultipleResponse";
import TableSheetQuestion from "../components/Survey/previewResponse/TableSheetQuestion";
import RatingResponse from "../components/Survey/previewResponse/RatingResponse";
import TextboxResponse from "../components/Survey/previewResponse/TextboxResponse";
import html2pdf from "html2pdf.js";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FilterIcon from "@mui/icons-material/FilterAltOutlined";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { useOutletContext } from "react-router";
import { QuestionType } from "@fhc/shared/constants";

import { createColumnHelper } from "@tanstack/react-table";
import Table from "../shared/components/Table";
import useTable, { createTableFilterFn } from "../shared/hooks/useTable";
import useCheckboxColumn from "../shared/components/Table/UseCheckboxColumn";
import useDateColumn from "../shared/hooks/useDateColumn";
import Button from "shared/components/Button";
import moreIcon from "shared/icon/DotsThreeOutline.svg";
import { downloadXLSX } from "utils/downloadXLSX";
import downloadIcon from "shared/icon/DownloadSimple.svg";
import FilterButton from "shared/components/Table/FilterButton";
import { FlashOnRounded } from "@mui/icons-material";
import Popover from "shared/components/Popover";
import Checkbox from "shared/components/Checkbox";
import DatePicker from "shared/components/DatePicker";

const downloadResponses = (responses) => {
    if (!responses || responses.length === 0) {
        return;
    }

    const excelData = [
        [
            "Email",
            "First Name",
            "Last Name",
            "Phone Number",
            "Self Assessed Financial Score",
            "Financial Score",
            "Date",
        ],
        ...responses.map((r) => [
            r.appointment?.email,
            r.appointment?.first_name,
            r.appointment?.last_name,
            r.appointment?.phone_number,
            r.survey_result.self_assessed_financial_score,
            r.survey_result.financial_score,
            moment(r.created_at).format("MMMM Do YYYY, h:mm:ss A"),
        ]),
    ];
    const columnWidths = [30, 20, 20, 12, 25, 12, 25];

    downloadXLSX(excelData, `FHS Responses - ${moment(new Date()).format("YYYY-MM-DD")}.xlsx`, columnWidths);
};

const columnHelper = createColumnHelper();

const QuestionResponses = () => {
    const { setBaseWidth } = useOutletContext();
    setBaseWidth("1400px");
    const [search, setSearch] = useState("");
    const [responses, setResponses] = useState({ rows: [], count: 0 });
    const [showResponse, setShowResponse] = useState(false);
    const [selectedResponse, setSelectedResponse] = useState({
        questions: [],
        responses: {},
        appointment: {},
    });

    // todo: don't hardcode here, at least move to a shared location
    // update: putting theme in session date (orgInfo) would fix this
    const [themeColors, setThemeColors] = useState({
        primaryColor: "#727BDC",
        buttonBackgroundColor: "#1DB19F",
        secondaryColor: "#B7BDFF",
        callToAction: {
            description: "",
            buttonText: "",
        },
    });

    useEffect(() => {
        async function onGetQuestionResponses() {
            try {
                const response = await appApi.get("/answer/all");

                if (response.data.success) {
                    setResponses(response.data.data);
                }
            } catch (error) {
                console.log({
                    message: "Error occurred retrieving responses",
                    error,
                });
            }
        }

        async function getTheme() {
            try {
                const response = await appApi.get("/surveys/theme");
                setThemeColors((theme) => ({ ...theme, ...response.data.theme }));
            } catch (_) {
                //just going to use default theme
            }
        }

        onGetQuestionResponses();
        getTheme();
    }, []);

    const selectUser = (event, row) => {
        if (event.target.type != "checkbox") {
            setSelectedResponse((selectedResponse) => ({
                ...selectedResponse,
                questions: row.questions,
                responses: row.responses,
                appointment: row.appointment,
                financialHealthScore: row.survey_result.financial_score,
            }));
            setShowResponse(() => true);
        }
    };

    /**
     * @param {HTMLElement} clone
     * @returns {HTMLElement}
     */
    const prepareClone = async (_doc, element) => {
        const info_container = element.querySelector("#info_container");
        const icon_profile_image = element.querySelector("#icon_profile_image");
        const questions = element.querySelectorAll(".questions");
        const notes = element.querySelector("#notes");
        notes.classList.remove("border");
        info_container.classList.add("w-full");
        icon_profile_image.classList.remove("h-32");
        icon_profile_image.classList.remove("w-32");
        icon_profile_image.classList.add("h-28");
        icon_profile_image.classList.add("w-28");
        element.style.width = "1280px";
        element.classList.remove("css-19ceord");

        questions.forEach((el) => {
            el.classList.remove("questions");
        });
    };

    const generatePdf = (respondentName) => {
        const page = document.getElementById("question_report");
        const style = document.createElement("style");
        document.head.appendChild(style);
        style.sheet?.insertRule("body > div:last-child img { display: inline-block; }");
        html2pdf()
            .from(page.innerHTML)
            .set({
                image: { type: "jpeg", quality: 1 },
                html2canvas: { scale: 2, onclone: prepareClone, letterRendering: true, dpi: 300 },
                jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
                pagebreak: {
                    avoid: ["tr", "p.question_content", "div.MuiTextField-root"],
                },
                filename: `${respondentName ? `${respondentName.toUpperCase()} - ` : ""}FHS Responses - [${moment(new Date()).format("YYYY-MM-DD")}]`,
            })
            .save()
            .then(() => {
                document.head.removeChild(style);
            });
    };

    /** @type {[import("@tanstack/react-table").RowSelectionState, React.Dispatch<React.SetStateAction<import("@tanstack/react-table").RowSelectionState>>]} */
    const [rowSelection, setRowSelection] = useState({});
    const checkboxColumn = useCheckboxColumn();
    const dateColumn = useDateColumn({
        id: "created_at",
        header: "Date",
        width: "10%",
    });
    const table = useTable({
        // FILTERING
        globalFilterState: {
            globalFilter: search,
            setGlobalFilter: setSearch,
        },
        globalFilterFn: createTableFilterFn([
            "name",
            "score",
            "phone",
            "email",
            "postal_code",
            "contact_method",
            "created_at",
            "collector",
            "bulk_upload",
        ]),
        defaultSort: [{ id: "created_at", desc: true }],
        getColumnCanGlobalFilter: (column) => {
            console.log(column);
            return Object.values(filterColumns).every((col) => col.value === false)
                ? true
                : (filterColumns[column.id]?.value ?? false);
        }, // Allow global filtering for all columns
        // SELECTION
        rowSelectionState: {
            rowSelection,
            setRowSelection,
        },
        //PAGINATION
        paginationOptions: {
            defaultPageIndex: 0,
            defaultPageSize: 7,
            disableGoToPage: true,
            disableChangePageSize: true,
        },
        // DATA/COLUMNS
        data: useMemo(
            () =>
                (responses?.rows ? responses.rows : []).map((response) => ({
                    ...response,
                    created_at: new Date(response.created_at),
                })),
            [responses.rows],
        ),
        columns: useMemo(
            () => [
                checkboxColumn,
                columnHelper.accessor(
                    (row) =>
                        row.survey_result?.respondent_name ??
                        (row.appointment ? `${row.appointment?.first_name} ${row.appointment?.last_name}` : "N/A"),
                    {
                        id: "name",
                        header: "Name",
                        width: "10%",
                    },
                ),
                columnHelper.accessor((row) => row.survey_result?.financial_score, {
                    id: "score",
                    header: "Score",
                    width: "72px",
                    cell: (info) => (
                        <span
                            className={`px-2 py-[6px] rounded-md ${
                                info?.getValue() < 200
                                    ? "text-red-600 bg-red-100"
                                    : info?.getValue() < 400
                                      ? "text-yellow-600 bg-yellow-100"
                                      : "text-green-600 bg-green-100"
                            }`}
                        >
                            {Number(info?.getValue()).toFixed(0)}
                        </span>
                    ),
                }),
                columnHelper.accessor((row) => row.appointment?.phone_number ?? "N/A", {
                    id: "phone",
                    header: "Phone Number",
                    width: "15%",
                }),
                columnHelper.accessor((row) => row.appointment?.email ?? "N/A", {
                    id: "email",
                    header: "Email Address",
                    width: "18%",
                }),
                columnHelper.accessor((row) => row.appointment?.postal_code ?? "N/A", {
                    id: "postal_code",
                    header: "Postal Code",
                    width: "10%",
                }),

                dateColumn,

                columnHelper.accessor((row) => row.appointment?.preferred_contact_method ?? "N/A", {
                    id: "contact_method",
                    header: "Contact by",
                    width: "100px",
                }),
                columnHelper.accessor((row) => row.survey_result?.question_external_link?.link_name, {
                    id: "collector",
                    header: "Collector",
                    width: "15%",
                }),
                columnHelper.accessor((row) => row.survey_result?.bulk_upload_respondent?.bulk_upload?.name ?? "N/A", {
                    id: "bulk_upload",
                    header: "Bulk Upload",
                    width: "15%",
                }),
                columnHelper.display({
                    id: "actions",
                    width: "64px",
                    cell: (info) => (
                        <Button
                            svg={moreIcon}
                            round
                            className="btn-secondary-gray btn-xs"
                            onClick={(e) => {
                                e.stopPropagation();
                                selectUser(e, info.row.original);
                            }}
                        />
                    ),
                }),
            ],
            [checkboxColumn, dateColumn],
        ),
    });

    const downloadAllResponses = () => {
        let filtered_responses = responses.rows;
        if (exportOptions.startDate.enabled && exportOptions.startDate.value !== null) {
            filtered_responses = filtered_responses.filter(
                (response) => new Date(response.created_at) >= exportOptions.startDate.value,
            );
        }
        if (exportOptions.endDate.enabled && exportOptions.endDate.value !== null) {
            const startOfNextDay = new Date(exportOptions.endDate.value);
            startOfNextDay.setDate(startOfNextDay.getDate() + 1);
            filtered_responses = filtered_responses.filter(
                (response) => new Date(response.created_at) < startOfNextDay,
            );
        }
        downloadResponses(filtered_responses);
        setExportPopupOpen(false);
    };

    const downloadSelectedResponses = () => {
        downloadResponses(
            Object.entries(rowSelection)
                .filter(([_, isSelected]) => isSelected) // ignore false values (not selected, not applicable for us rn but possible with other selection setups)
                .map(([i, _]) => responses.rows[i]),
        );
    };

    const [filterColumns, setFilterColumns] = useState({
        name: {
            label: "Name",
            value: false,
        },
        score: {
            label: "Score",
            value: false,
        },
        collector: {
            label: "Collector",
            value: false,
        },
        bulk_upload: {
            label: "Bulk Upload",
            value: false,
        },
    });

    const [exportOptions, setExportOptions] = useState({
        startDate: {
            enabled: false,
            value: null,
        },
        endDate: {
            enabled: false,
            value: null,
        },
    });

    const [exportPopupOpen, setExportPopupOpen] = useState(false);

    return (
        <AppLayout>
            <Box className="mx-[46px] mb-[11px] border border-slate-200 py-7 rounded-lg bg-white  ">
                <Box mx={4}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: "100%", my: 4 }}
                    >
                        <div className="flex flex-col">
                            <div className="flex gap-4 items-baseline w-full">
                                <div className="font-bold text-2xl text-slate-600">Responses</div>
                                <div className="ml-auto rounded-lg bg-slate-200 px-3 py-1 text-slate-600 text-sm w-fit h-fit">
                                    {responses.count}
                                </div>
                            </div>
                            <div className="flex gap-4 items-baseline">
                                <div className="font-bold text-2xl text-slate-600">Appointments</div>
                                <div className="ml-auto rounded-lg bg-slate-200  px-3 py-1 text-slate-600 text-sm w-fit h-fit">
                                    {responses.booked_appointment_count}
                                </div>
                            </div>
                        </div>
                        <Box display="flex" gap={1} alignItems="center">
                            <Popover
                                isOpen={exportPopupOpen}
                                showArrow
                                content={
                                    <div className="flex flex-col gap-2">
                                        <div className="grid grid-cols-[auto_1fr] gap-2 grid-flow-dense">
                                            <Checkbox
                                                className="text-xs py-1"
                                                label="From"
                                                value={exportOptions.startDate.enabled}
                                                onChange={(value) =>
                                                    setExportOptions({
                                                        ...exportOptions,
                                                        startDate: { ...exportOptions.startDate, enabled: value },
                                                    })
                                                }
                                            />
                                            <DatePicker
                                                value={exportOptions.startDate.value}
                                                onChange={(value) =>
                                                    setExportOptions({
                                                        ...exportOptions,
                                                        startDate: { enabled: value !== null, value },
                                                    })
                                                }
                                            />
                                            <Checkbox
                                                className="text-xs py-1"
                                                label="To"
                                                value={exportOptions.endDate.enabled}
                                                onChange={(value) =>
                                                    setExportOptions({
                                                        ...exportOptions,
                                                        endDate: { ...exportOptions.endDate, enabled: value },
                                                    })
                                                }
                                            />
                                            <DatePicker
                                                value={exportOptions.endDate.value}
                                                onChange={(value) =>
                                                    setExportOptions({
                                                        ...exportOptions,
                                                        endDate: { enabled: value !== null, value },
                                                    })
                                                }
                                            />
                                        </div>
                                        <Button
                                            svg={downloadIcon}
                                            className="btn-primary btn-xs text-sm"
                                            onClick={downloadAllResponses}
                                            disabled={
                                                exportOptions.startDate.enabled &&
                                                exportOptions.endDate.enabled &&
                                                exportOptions.startDate.value > exportOptions.endDate.value
                                            }
                                        >
                                            Export
                                        </Button>
                                    </div>
                                }
                            >
                                <Button
                                    className="btn-secondary-gray btn-xs"
                                    onClick={() => setExportPopupOpen(!exportPopupOpen)}
                                >
                                    Export all...
                                </Button>
                            </Popover>
                            <Button
                                className="btn-secondary-gray btn-xs"
                                svg={downloadIcon}
                                disabled={Object.keys(rowSelection).length === 0}
                                onClick={() => downloadSelectedResponses()}
                            >
                                Export selected
                            </Button>
                            <div className="border border-[#D7DFE9] h-[38.5px] w-[256px] pl-3 py-1.5 rounded-md flex items-center space-x-2">
                                <SearchOutlinedIcon className="text-slate-600 text-lg"></SearchOutlinedIcon>
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full outline-none text-xs"
                                />
                            </div>
                            <FilterButton filterColumns={filterColumns} setFilterColumns={setFilterColumns} />
                            {/* <PDFResponses></PDFResponses> */}
                        </Box>
                    </Box>
                </Box>

                <div style={{ margin: "0px 32px" }}>
                    <Table table={table} noDataComponent="No responses available" />
                </div>

                <Modal
                    open={showResponse}
                    onClose={() => setShowResponse(() => false)}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                    sx={{
                        "& ::-webkit-scrollbar": {
                            width: 5,
                        },
                        "& ::-webkit-scrollbar-track": {
                            background: "transparent",
                        },
                        "& ::-webkit-scrollbar-thumb": {
                            background: "#455468",
                            borderRadius: 10,
                        },
                    }}
                >
                    <Box
                        id="question_report"
                        sx={{
                            background: "white",
                            m: 4,
                            mx: "auto",
                            maxWidth: 1000,
                            borderRadius: 2,
                            maxHeight: "90vh",
                            overflowY: "auto",
                        }}
                    >
                        <Box>
                            <div className="px-10 pt-7">
                                <div className="flex justify-end w-full">
                                    <IconContentButton
                                        size="xs"
                                        colorTheme="secondary"
                                        iconComponent={<downloadIcon />}
                                        content="Export as PDF"
                                        handleOnClick={() =>
                                            generatePdf(
                                                selectedResponse.appointment?.first_name &&
                                                    selectedResponse.appointment?.last_name &&
                                                    `${selectedResponse.appointment?.first_name} ${selectedResponse.appointment?.last_name}`,
                                            )
                                        }
                                        data-html2canvas-ignore="true"
                                    />
                                </div>
                                <div className="my-5 flex gap-4">
                                    <div>
                                        <div
                                            id="icon_profile_image"
                                            className="w-32 h-32 flex items-center justify-center rounded-full bg-slate-200"
                                        >
                                            <PersonOutlineIcon className="text-slate-600 text-6xl"></PersonOutlineIcon>
                                        </div>
                                    </div>
                                    <div className="flex-1 w-full">
                                        <div className="flex items-baseline space-x-4 mb-2 text-slate-600">
                                            <div className="text-2xl font-bold ">Lead details</div>
                                            <div className="text-sm">
                                                {moment(selectedResponse.created_at).format("MMM D, YYYY")}
                                            </div>
                                        </div>
                                        <div
                                            className="flex justify-between w-full text-slate-600 "
                                            id="info_container"
                                        >
                                            <div className="space-y-2">
                                                <div className="font-normal">
                                                    <strong>First Name:</strong>
                                                    <span> {selectedResponse.appointment?.first_name ?? "N/A"}</span>
                                                </div>
                                                <div className="font-normal">
                                                    <strong>Last Name:</strong>
                                                    <span>{selectedResponse.appointment?.last_name ?? "N/A"}</span>
                                                </div>
                                                <div className="font-normal">
                                                    <strong>Email:</strong>{" "}
                                                    <span>{selectedResponse.appointment?.email ?? "N/A"}</span>
                                                </div>

                                                <div className="font-normal">
                                                    <strong className="mr-1">Score:</strong>
                                                    <span
                                                        className={`font-semibold px-3 py-1 rounded-lg 
                            ${
                                selectedResponse.financialHealthScore <= 200
                                    ? "text-red-600 bg-red-100"
                                    : selectedResponse.financialHealthScore <= 400
                                      ? "text-yellow-600 bg-yellow-100"
                                      : selectedResponse.financialHealthScore <= 1000
                                        ? "text-green-600 bg-green-100"
                                        : ""
                            }
                          `}
                                                    >
                                                        {selectedResponse.financialHealthScore &&
                                                            Number(selectedResponse.financialHealthScore).toFixed(0)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="font-normal">
                                                    <strong>Postal code:</strong>{" "}
                                                    <span>{selectedResponse.appointment?.postal_code ?? "N/A"}</span>
                                                </div>
                                                <div className="font-normal">
                                                    <strong>Phone:</strong>{" "}
                                                    <span>{selectedResponse.appointment?.phone_number ?? "N/A"}</span>
                                                </div>
                                                <div className="font-normal">
                                                    <strong>Contact Method:</strong>{" "}
                                                    <span>
                                                        {selectedResponse.appointment?.preferred_contact_method ??
                                                            "N/A"}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div>
                                                    <strong>Subscriber:</strong>{" "}
                                                    <span className="font-normal">Yes</span>
                                                </div>
                                                <div>
                                                    <strong>Note:</strong>
                                                    <div className="mt-2">
                                                        <textarea
                                                            id="notes"
                                                            className="border outline-none rounded-lg p-3 text-sm "
                                                            name=""
                                                            cols="20"
                                                            rows="3"
                                                        ></textarea>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr />
                            <Box sx={{ mt: 3, p: 5, py: 0 }} className="text-slate-600">
                                <div className="text-slate-600 text-2xl font-semibold mb-5">Question answers</div>

                                {selectedResponse.questions.map((question, index) => (
                                    <div className="questions" key={`response-row-${index}`}>
                                        <p
                                            className="question_content mb-[13px]"
                                            dangerouslySetInnerHTML={{ __html: sanitize(question.question) }}
                                        />
                                        <div className="answer_content ">
                                            {selectedResponse.responses[question.id] ? (
                                                question.type === QuestionType.SINGLE_RESPONSE ||
                                                question.type === QuestionType.MULTIPLE_RESPONSE ? (
                                                    <SingleMultipleResponse
                                                        question={question}
                                                        selections={selectedResponse.responses}
                                                        key={question.id}
                                                        themeColors={themeColors}
                                                    ></SingleMultipleResponse>
                                                ) : question.type === QuestionType.RATING_SCALE ? (
                                                    <RatingResponse
                                                        question={question}
                                                        selections={selectedResponse.responses}
                                                        key={question.id}
                                                        themeColors={themeColors}
                                                    ></RatingResponse>
                                                ) : question.type === QuestionType.TEXTBOX ? (
                                                    <TextboxResponse
                                                        question={question}
                                                        selections={selectedResponse.responses}
                                                        key={question.id}
                                                        themeColors={themeColors}
                                                    ></TextboxResponse>
                                                ) : (
                                                    <TableSheetQuestion
                                                        question={question}
                                                        selections={selectedResponse.responses}
                                                        key={question.id}
                                                        themeColors={themeColors}
                                                    ></TableSheetQuestion>
                                                )
                                            ) : (
                                                <Box sx={{ mb: 5 }}>
                                                    <Typography>None</Typography>
                                                </Box>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        </AppLayout>
    );
};

export default QuestionResponses;
